import { Document, Page, View, Text, Image, Font } from '@react-pdf/renderer';
import { styles } from './pdfStyle';
import logo from '../../assets/evalLogo.png';
import { theme } from '../../styles/styles';
import AlbertSansRegular from '../../assets/fonts/AlbertSans-Regular.ttf';
import AlbertSansMedium from '../../assets/fonts/AlbertSans-Medium.ttf';
import AlbertSansSemiBold from '../../assets/fonts/AlbertSans-SemiBold.ttf';
import AlbertSansBold from '../../assets/fonts/AlbertSans-Bold.ttf';
import AlbertSansExtraBold from '../../assets/fonts/AlbertSans-ExtraBold.ttf';
import { chargingAtHomeOptions } from '../../features/inputs/inputOptions';

Font.register({
  family: 'Albert Sans',
  fonts: [
    { src: AlbertSansRegular, fontWeight: 400 },
    { src: AlbertSansMedium, fontWeight: 500 },
    { src: AlbertSansSemiBold, fontWeight: 600 },
    { src: AlbertSansBold, fontWeight: 700 },
    { src: AlbertSansExtraBold, fontWeight: 800 },
  ],
});

const stoplightColor = (value) => {
  if (value === 'Green') return theme.stoplightGreen;
  if (value === 'Yellow') return theme.stoplightYellow;
  return theme.stoplightRed;
};

const formatCurrency = (value) => {
  const abs = Math.abs(value ?? 0).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  });
  return (value ?? 0) < 0 ? `-$${abs}` : `+$${abs}`;
};

const formatNet = (value) => {
  return (value ?? 0).toLocaleString('en-US', {
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'USD',
  });
};

const PDFHeader = () => (
  <View style={styles.header}>
    <Image src={logo} style={styles.logo} />
    <View style={styles.innerHeader}>
      <Text>EV Workplace Charging Tool</Text>
    </View>
  </View>
);

export default function PDFDocument({ inputs, activeResult, requestData }) {
  const yearlyItems = [
    { label: 'Electricity', value: activeResult?.yearly_electrcity_cost },
    {
      label: 'Operations & Maintenance',
      value: activeResult?.yearly_op_maint_cost,
    },
    { label: 'Financing', value: activeResult?.yearly_financing_cost },
    {
      label: 'Charging Revenue',
      value: activeResult?.yearly_charging_revenue,
      isRevenue: true,
    },
  ];

  const onetimeItems = [
    { label: 'Equipment', value: activeResult?.onetime_equipment },
    { label: 'Installation', value: activeResult?.onetime_installation },
    {
      label: 'Incentives',
      value: activeResult?.onetime_incentive,
      isRevenue: true,
    },
    { label: '', value: '\u00A0', empty: true },
  ];

  const yearlyNet =
    (activeResult?.yearly_electrcity_cost ?? 0) +
    (activeResult?.yearly_op_maint_cost ?? 0) +
    (activeResult?.yearly_financing_cost ?? 0) +
    (activeResult?.yearly_charging_revenue ?? 0);

  const onetimeNet =
    (activeResult?.onetime_equipment ?? 0) +
    (activeResult?.onetime_installation ?? 0) +
    (activeResult?.onetime_incentive ?? 0);

  return (
    <Document
      title="Workplace Charging Analysis"
      author="EV Workplace Charging Tool"
    >
      <Page size="A4" style={styles.page}>
        {/* ── Page 1 ── */}
        <PDFHeader />
        <View style={styles.mainPage}>
          <Text style={styles.titleText}>Workplace Charging Analysis</Text>
          <Text style={styles.subheader}>Inputs</Text>

          <View style={styles.grid}>
            {[
              [
                { label: 'State', value: inputs?.state ?? '—' },
                {
                  label: 'Total Employees',
                  value: inputs?.numOfEmployees ?? '—',
                },
              ],
              [
                {
                  label: 'Avg Daily Attendance',
                  value:
                    inputs?.avgDailyAttenance != null
                      ? `${inputs.avgDailyAttenance}%`
                      : '—',
                },
                {
                  label: 'Car Commuter Share',
                  value: inputs?.carShare != null ? `${inputs.carShare}%` : '—',
                },
              ],
              [
                {
                  label: 'EV Commuter Share',
                  value: inputs?.evShare != null ? `${inputs.evShare}%` : '—',
                },
                {
                  label: 'Employees with Home Charging',
                  value:
                    chargingAtHomeOptions.find(
                      (o) => o.value === inputs?.chargingAtHome,
                    )?.label ?? '—',
                },
              ],
              [
                {
                  label: 'Charging Ports to Install',
                  value: inputs?.chargingPorts ?? '—',
                },
                {
                  label: 'Existing Charging Ports',
                  value: inputs?.existingChargers ?? '—',
                },
              ],
              [
                {
                  label: 'Ownership Model',
                  value:
                    inputs?.ownershipModel != null
                      ? `${inputs?.ownershipModel}`
                      : '—',
                },
                {
                  label: 'Charging Type',
                  value:
                    inputs?.equipmentType != null
                      ? `${inputs?.equipmentType}`
                      : '—',
                },
              ],
              [
                {
                  label: 'Open to the Public',
                  value: inputs?.openToPublic ?? '—',
                },
                {
                  label: 'Per User Electricity Rate',
                  value:
                    inputs?.ownershipModel === 'Charging-as-a-Service'
                      ? '—'
                      : `$${(inputs?.chargingFee ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per kWh`,
                },
              ],
            ].map((row, i, arr) => (
              <View
                key={i}
                style={[
                  styles.gridRow,
                  i === arr.length - 1 ? { borderBottom: 'none' } : {},
                ]}
              >
                <View style={styles.gridCell}>
                  <Text style={styles.gridTitle}>{row[0].label}</Text>
                  <Text style={styles.gridValue}>{String(row[0].value)}</Text>
                </View>
                <View style={styles.gridCellDivider} />
                <View style={styles.gridCell}>
                  <Text style={styles.gridTitle}>{row[1].label}</Text>
                  <Text style={styles.gridValue}>{String(row[1].value)}</Text>
                </View>
              </View>
            ))}
          </View>

          <Text style={[styles.subheader, { marginTop: 14 }]}>
            Program Benefits
          </Text>

          {/* Employee Amenity Value */}
          <View style={styles.benefitSection}>
            <View style={styles.benefitRow}>
              <View
                style={[
                  styles.stoplightDot,
                  {
                    backgroundColor: stoplightColor(
                      activeResult?.amenity_stoplight,
                    ),
                  },
                ]}
              />
              <Text style={styles.benefitTitle}>Employee Amenity Value</Text>
            </View>
            <Text style={styles.benefitText}>
              {activeResult?.amenity_rationale ?? '—'}
            </Text>
          </View>

          {/* GHG Emissions */}
          <View style={styles.benefitSection}>
            <View style={styles.benefitRow}>
              <Text style={styles.benefitTitle}>GHG Emissions Estimate</Text>
            </View>
            <Text style={styles.benefitText}>
              Workplace EV charging could avoid roughly{' '}
              <Text style={styles.benefitTextBold}>
                {Math.round(
                  activeResult?.ghg_avoided_tons ?? 0,
                ).toLocaleString()}{' '}
                tons
              </Text>{' '}
              of CO2e emissions (equivalent to{' '}
              <Text style={styles.benefitTextBold}>
                {(activeResult?.miles_driven ?? 0).toLocaleString()} miles
              </Text>{' '}
              driven or{' '}
              <Text style={styles.benefitTextBold}>
                {(activeResult?.gallons_gas ?? 0).toLocaleString()} gallons
              </Text>{' '}
              of gasoline) using{' '}
              <Text style={styles.benefitTextBold}>
                {Math.round(
                  (activeResult?.annual_energy_kwh ?? 0) / 1000,
                ).toLocaleString()}{' '}
                MWh
              </Text>{' '}
              of energy per year.
            </Text>
          </View>
          <Text style={[styles.subheader, { marginTop: 14 }]}>
            Revenue and Costs Estimates
          </Text>

          {inputs.ownershipModel === 'Charging-as-a-Service' ? (
            <View style={styles.monthlyCostColumns}>
              {/* Monthly Costs */}
              <View style={styles.costColumn}>
                <View style={styles.costRow}>
                  <Text style={styles.costTotal}>Monthly Costs</Text>
                  <Text style={styles.costTotal}>
                    {formatNet(requestData.monthly_caas_cost)}
                  </Text>
                </View>
              </View>
            </View>
          ) : (
            <View style={styles.costColumns}>
              {/* Yearly Costs */}
              <View style={styles.costColumn}>
                <Text style={styles.costColumnTitle}>Yearly Costs</Text>
                <View style={styles.costDivider} />
                {yearlyItems.map(({ label, value, isRevenue }) => (
                  <View key={label} style={styles.costRow}>
                    <Text style={styles.costLabel}>{label}</Text>
                    <Text
                      style={isRevenue ? styles.costRevenue : styles.costAmount}
                    >
                      {formatCurrency(value)}
                    </Text>
                  </View>
                ))}
                <View style={styles.costDivider} />
                <View style={styles.costRow}>
                  <Text style={styles.costTotal}>Net Yearly</Text>
                  <Text style={styles.costTotal}>{formatNet(yearlyNet)}</Text>
                </View>
              </View>

              {/* One-Time Costs */}
              <View style={styles.costColumn}>
                <Text style={styles.costColumnTitle}>One-Time Costs</Text>
                <View style={styles.costDivider} />
                {onetimeItems.map(({ label, value, isRevenue, empty }, i) => (
                  <View key={i} style={styles.costRow}>
                    <Text style={styles.costLabel}>{label}</Text>
                    <Text
                      style={isRevenue ? styles.costRevenue : styles.costAmount}
                    >
                      {empty ? '\u00A0' : formatCurrency(value)}
                    </Text>
                  </View>
                ))}
                <View style={styles.costDivider} />
                <View style={styles.costRow}>
                  <Text style={styles.costTotal}>Net One-Time</Text>
                  <Text style={styles.costTotal}>{formatNet(onetimeNet)}</Text>
                </View>
              </View>
            </View>
          )}
          {inputs.ownershipModel === 'Charging-as-a-Service' && (
            <View style={styles.caasInfoBox}>
              <Text style={styles.caasInfoBoxTitle}>
                About Charging as a Service (CaaS)
              </Text>
              <Text style={styles.caasInfoBoxText}>
                While CaaS contracts are meant to eliminate upfront costs
                through long-term fixed monthly payments (assumed 5 years for
                this tool), depending on the company&apos;s policies when
                offering such contracts, there may still be upfront costs.
              </Text>
              <Text style={[styles.caasInfoBoxText, { marginTop: 6 }]}>
                Additionally, revenue from chargers is likely to be split
                between the charging company and the property owner, depending
                on contract terms. Many CaaS contracts are customizable to the
                property owner&apos;s needs, from capping the per-kWh price to
                shorter or longer contract lengths, but may result in higher
                monthly payments or upfront costs.
              </Text>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
}
